import { useCallback, useEffect, useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { useFormik } from 'formik'

import { ISpinHistoryFiltersReq } from 'shared/models/Wheel/Wheel.types'

import { getSpinHistory } from 'features/wheelGame/actions'
import { selectSpinHistory, selectWheelCommunication } from 'features/wheelGame/selectors'

import { usePagination } from 'components/UIKit'

export type SpinHistoryFilters = {
  periodStart: Date
  periodEnd: Date
}

export const useSpinHistory = () => {
  const dispatch = useDispatch()
  const { list: spinHistory, count } = useSelector(selectSpinHistory, shallowEqual)
  const { isLoading, isSuccess, error } = useSelector(selectWheelCommunication, shallowEqual).getSpinHistory

  const pagination = usePagination({
    initialPage: 1,
    initialPageSize: 10,
    totalItems: count,
  })

  const fetchHistory = useCallback((values: SpinHistoryFilters, page?: number) => {
    const payload: ISpinHistoryFiltersReq = {
      page: page ?? pagination.page,
      page_size: pagination.pageSize,
      date_from: dayjs(values.periodStart).startOf('day').format('YYYY-MM-DDTHH:mmZ'),
      date_to: dayjs(values.periodEnd).endOf('day').format('YYYY-MM-DDTHH:mmZ'),
      order_by: '-date',
    }

    dispatch(getSpinHistory(payload))
  }, [dispatch, pagination.page, pagination.pageSize])

  const formik = useFormik<SpinHistoryFilters>({
    initialValues: {
      periodStart: dayjs(new Date()).startOf('week').subtract(1, 'week').toDate(),
      periodEnd: new Date(),
    },
    onSubmit: values => {
      pagination.onChangePage(1)
      fetchHistory(values, 1)
    },
  })
  const formValuesRef = useRef(formik.values)

  useEffect(() => {
    formValuesRef.current = formik.values
  }, [formik.values])

  useEffect(() => {
    fetchHistory(formValuesRef.current)
  }, [fetchHistory, pagination.page])

  return {
    formik,
    spinHistory,
    pagination,
    isLoading,
    isSuccess,
    error,
  }
}
